/**
 * 换我出镜参考视频「疑似转发/转码件」识别(0924 车 D 一期止血)—— **只警示,不拦**。
 *
 * 0915 对照单:微信转发件 / 下载站转码件(容器里带 `wxMeta` 或 FFmpeg 的 `Lavf` 编码器标)
 * 与低码率件在上游审核被拒的比例远高于手机原件。前端认不准「一定会被拒」,所以只给一句提醒,
 * 让用户知道换原件更稳;真被拒时不扣费(上游拒 = 未出片)。
 *
 * 判据三条,任一命中即警示:
 *   ① 文件头/尾字节里有 `wxMeta`(微信转码写的元数据盒名)或其值里的版本键 `WXVer`
 *     (0925 收尾波 A13 补:ffprobe 实测转发件是 `wxMeta:{"WXVer":"..."}`,两个字符串
 *     只要命中其一都判同一个信号,udta/moov 盒位置不定,不强求两者都出现);
 *   ② 有 `Lavf`(FFmpeg libavformat 写的 encoder 标签 —— 手机原生相机不会写它);
 *   ③ 整体码率 < 1.5 Mbps(手机原件 1080p 通常 8~20 Mbps,微信压缩后常在 1 Mbps 上下)。
 * moov 盒可能在文件头也可能在尾部,所以头尾各读一段,不读全文件(参考视频可能上百 MB)。
 */
export const FORWARDED_LOW_BITRATE_BPS = 1_500_000;
const SNIFF_BYTES = 256 * 1024;

export type ForwardedSignal = "wxMeta" | "lavf" | "lowBitrate";

/** 纯函数:给字节文本(latin1)+ 体积 + 时长,判出命中了哪几条。 */
export function detectForwardedSignals(
  bytesText: string,
  sizeBytes: number,
  durationSec: number | null | undefined
): ForwardedSignal[] {
  const hits: ForwardedSignal[] = [];
  if (bytesText.includes("wxMeta") || bytesText.includes("WXVer")) hits.push("wxMeta");
  if (bytesText.includes("Lavf")) hits.push("lavf");
  if (durationSec && durationSec > 0 && sizeBytes > 0) {
    const bps = (sizeBytes * 8) / durationSec;
    if (bps < FORWARDED_LOW_BITRATE_BPS) hits.push("lowBitrate");
  }
  return hits;
}

export function forwardedHintText(signals: readonly ForwardedSignal[]): string | null {
  if (signals.length === 0) return null;
  const why = signals.includes("wxMeta")
    ? "像是微信转发过的视频"
    : signals.includes("lavf")
      ? "像是被软件转码/剪辑过的视频"
      : "画质码率偏低,像是转发或压缩过的视频";
  return `这段${why},上游审核更容易拒。最稳的是用手机相机直接拍的原件(被拒不扣费)。`;
}

async function readBlobText(blob: Blob): Promise<string> {
  const buf =
    typeof blob.arrayBuffer === "function"
      ? await blob.arrayBuffer()
      : await new Response(blob).arrayBuffer();
  return new TextDecoder("latin1").decode(new Uint8Array(buf));
}

/** IO 包装:读文件头尾各 256KB。读失败(老浏览器/权限)→ 只按码率判,不抛。 */
export async function sniffForwardedVideo(
  file: File,
  durationSec: number | null | undefined
): Promise<ForwardedSignal[]> {
  let text = "";
  try {
    const head = file.slice(0, SNIFF_BYTES);
    const tail =
      file.size > SNIFF_BYTES ? file.slice(Math.max(SNIFF_BYTES, file.size - SNIFF_BYTES)) : null;
    text = (await readBlobText(head)) + (tail ? await readBlobText(tail) : "");
  } catch {
    text = "";
  }
  return detectForwardedSignals(text, file.size, durationSec);
}
